import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { radioCardVariants } from "../../DesignSystem/index.styles";
import { Typography } from "../../DesignSystem";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "@/components/ui/label";

export interface RadioCardOption {
  label: string;
  value: string;
  icon?: string;
  description?: string;
}

interface RadioCardGroupProps {
  title?: string;
  options: RadioCardOption[];
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  className?: string;
  columns?: 1 | 2 | 3 | 4;
}

const RadioCardGroup = forwardRef<HTMLButtonElement, RadioCardGroupProps>(
  (
    { title, options, name, value, onChange, error, className, columns = 2 },
    ref
  ) => {
    const gridCols = {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
    };

    return (
      <div className={className}>
        {title && (
          <Typography variant="h4" color="primary" className="mb-6">
            {title}
          </Typography>
        )}
        <RadioGroup
          value={value}
          onValueChange={onChange}
          className={cn(gridCols[columns])}
        >
          {options.map((option) => (
            <Label
              key={option.value}
              htmlFor={`${name}-${option.value}`}
              className={cn(
                radioCardVariants({
                  selected: value === option.value,
                }),
                "relative"
              )}
            >
              <RadioGroupItem
                ref={ref}
                value={option.value}
                id={`${name}-${option.value}`}
                className="sr-only"
              />
              <div className="flex flex-col items-center justify-center gap-2">
                {option.icon && (
                  <img
                    src={option.icon}
                    alt={option.label}
                    className="w-12 h-12"
                  />
                )}
                <span className="text-sm font-medium">{option.label}</span>
                {option.description && (
                  <span className="text-xs text-center opacity-80">
                    {option.description}
                  </span>
                )}
              </div>
            </Label>
          ))}
        </RadioGroup>
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    );
  }
);

RadioCardGroup.displayName = "RadioCardGroup";

export default RadioCardGroup;
